import { useEffect, useState } from "react";
import { api, num, fmtDate, API_URL } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Paged } from "@/components/Pager";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, ChevronDown, Factory, CalendarDays, Package, Building2, Printer } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ProductReadOnlyCards } from "@/components/ProductReadOnlyCards";

const STATUS_STYLES = {
  "Pending": "bg-slate-100 text-slate-700",
  "In Progress": "bg-blue-50 text-blue-700",
  "Completed": "bg-emerald-50 text-emerald-700",
};
const ACCENT = { "Pending": "#94A3B8", "In Progress": "#2563EB", "Completed": "#10B981" };

const JobCard = ({ j, expanded, onToggle, onDelete }) => {
  const items = j.items?.length ? j.items : [{ description: j.product_description, color: "", cut_length: "", size: "", quantity: j.quantity, unit: "" }];
  const inHouse = j.vendor_name === "In-House";
  return (
    <div className={`border rounded-lg bg-white overflow-hidden transition-shadow duration-200 ${expanded ? "shadow-md border-slate-300" : "border-slate-200 hover:shadow-sm"}`}
         data-testid={`jc-card-${j.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`jc-toggle-${j.id}`}>
        <span className="w-1 self-stretch rounded-full flex-none" style={{ background: ACCENT[j.status] || "#94A3B8" }} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 truncate" data-testid={`jc-company-${j.id}`}>{j.customer_name || j.order_number || j.number}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-slate-600">{j.number}</span>
            {j.order_number && <span className="font-mono">Order: {j.order_number}</span>}
            <span className="inline-flex items-center gap-1"><Package size={10} /> {items.length} item{items.length > 1 ? "s" : ""} · {num(j.quantity)}</span>
          </div>
        </div>
        {inHouse && (
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-medium flex-none">
            <Factory size={10} /> In-House
          </span>
        )}
        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider flex-none ${STATUS_STYLES[j.status] || "bg-slate-100 text-slate-700"}`} data-testid={`jc-status-${j.id}`}>{j.status}</span>
        <ChevronDown size={16} className={`text-slate-400 flex-none transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }} className="overflow-hidden" data-testid={`jc-details-${j.id}`}>
            <div className="px-4 pb-4 pt-1 border-t border-slate-100">
              <div className="mt-3">
                <ProductReadOnlyCards items={items} showPricing={false} compact label={null} testPrefix={`jc-item-${j.id}`} />
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1.5 font-medium text-slate-800" data-testid={`jc-detail-company-${j.id}`}><Building2 size={12} className="text-slate-400" /> {j.customer_name || "-"}</span>
                <span className="inline-flex items-center gap-1.5"><Factory size={12} className="text-slate-400" /> {j.vendor_name || "-"}</span>
                <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} className="text-slate-400" /> {j.delivery_date ? fmtDate(j.delivery_date) : "No delivery date"}</span>
                {j.po_number && <span className="font-mono text-slate-500">PO: {j.po_number}</span>}
                <a href={`${API_URL}/jobcards/${j.id}/pdf`} target="_blank" rel="noreferrer"
                   onClick={(e) => e.stopPropagation()}
                   className="ml-auto inline-flex items-center gap-1 text-slate-500 hover:text-blue-600 transition-colors"
                   title="Print job card" data-testid={`jc-print-${j.id}`}>
                  <Printer size={13} /> Print
                </a>
                <button onClick={(e) => { e.stopPropagation(); onDelete(j); }}
                        className="inline-flex items-center gap-1 text-slate-400 hover:text-red-500 transition-colors"
                        title="Delete job card" data-testid={`jc-delete-${j.id}`}>
                  <Trash2 size={13} /> Delete
                </button>
              </div>
              {j.notes && <div className="text-[11px] text-slate-400 mt-2">{j.notes}</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CardList = ({ rows, emptyMsg, expandedId, setExpandedId, onDelete }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
    {rows.map((j) => (
      <JobCard key={j.id} j={j} expanded={expandedId === j.id}
               onToggle={() => setExpandedId(expandedId === j.id ? null : j.id)} onDelete={onDelete} />
    ))}
    {rows.length === 0 && (
      <div className="col-span-full text-center py-14 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">
        {emptyMsg}
      </div>
    )}
  </div>
);

export default function Production() {
  const [list, setList] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const load = () => api.get("/jobcards").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const onDelete = async (j) => {
    if (!window.confirm(`Delete job card ${j.number}?`)) return;
    try { await api.delete(`/jobcards/${j.id}`); toast.success("Job card deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const active = list.filter((j) => j.status !== "Completed");
  const completed = list.filter((j) => j.status === "Completed");

  return (
    <div className="space-y-4" data-testid="production-page">
      <PageHeader title="Production" subtitle="Job cards go In Progress the moment they are created (vendor PO or in-house) · LR saved → Completed · click a card for details" />

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active" data-testid="jc-tab-active">Active Job Cards ({active.length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="jc-tab-completed">Completed Job Cards ({completed.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="active" className="mt-4">
          <Paged items={active} perPage={12}>
            {(pageRows, pagerEl) => (<>
              <CardList rows={pageRows} expandedId={expandedId} setExpandedId={setExpandedId} onDelete={onDelete}
                        emptyMsg="No active job cards — they are created automatically from Purchase Orders or in-house orders." />
              <div className="mt-3 border border-slate-200 rounded-md overflow-hidden empty:hidden">{pagerEl}</div>
            </>)}
          </Paged>
        </TabsContent>
        <TabsContent value="completed" className="mt-4">
          <Paged items={completed} perPage={12}>
            {(pageRows, pagerEl) => (<>
              <CardList rows={pageRows} expandedId={expandedId} setExpandedId={setExpandedId} onDelete={onDelete}
                        emptyMsg="No completed job cards yet." />
              <div className="mt-3 border border-slate-200 rounded-md overflow-hidden empty:hidden">{pagerEl}</div>
            </>)}
          </Paged>
        </TabsContent>
      </Tabs>
    </div>
  );
}
